import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Expo, TimelineLite, TimelineMax, TweenLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list } from "Utils/Reflection";

import { ch7_p03 } from "../../../layouts/ch7_p03/ch7_p03";

export class Chapter7Page3 extends AbstractStoryState implements ReturnType<typeof ch7_p03> {

    readonly bg = this.addChild(new SceneSprite());
    readonly bubbleBlur = this.addChild(new SceneSprite());
    readonly bubble = this.addChild(new SceneSprite());
    readonly holly = this.addChild(new SceneSprite());

    readonly frameLeft = this.addChild(SceneSprite.withPrefix("ch7_p03/"));
    readonly frameRight = this.addChild(SceneSprite.withPrefix("ch7_p03/"));
    readonly linesLeft = this.addChild(SceneSprite.withPrefix("ch7_p03/"));
    readonly linesRight = this.addChild(SceneSprite.withPrefix("ch7_p03/"));

    public init(pSize: PIXI.Point, pData: any) {
        // exported layouts from Animate CC
        ch7_p03.call(this);
        this.frameLeft.visible = false;
        this.frameRight.visible = false;

        const scratchSound = "";
        const revealSound = "";

        const onScratchComplete = () => {
            TweenLite.delayedCall(1.5, () => {
                this.gotoNextState({ transition: "down" });
            });
        };

        // super.init calls onResize
        super.init(pSize, pData);

        this.makeScratchable(this.bubbleBlur, this.bubble, scratchSound, revealSound, onScratchComplete);
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
    }

    public animateIn(pOnComplete: () => void) {
        super.animateIn(pOnComplete);
    }

    public animateOut(pOnComplete: () => void) {
        super.animateOut(pOnComplete);
    }

    public onAnimateInComplete() {
        super.onAnimateInComplete();

    }

    makeScratchable(
        initialImage: PIXI.Sprite,
        hiddenImage: PIXI.Sprite,
        scratchSound: string,
        revealSound: string,
        onComplete: () => void,
    ) {
        const threshold = 0.75;

        const sound = HLF.Application.instance.audio.createAudioTrack(scratchSound, HLF.AudioCategory.SFX);
        const sound2 = HLF.Application.instance.audio.createAudioTrack(revealSound, HLF.AudioCategory.SFX);

        // make rendertexture same size as hidden image
        const renderTexture = PIXI.RenderTexture.create({
            width: hiddenImage.width,
            height: hiddenImage.height,
            resolution: 0.25,
        });

        // position mask over image, but don't use mask.anchor
        const mask = this.addChild(new PIXI.Sprite(renderTexture));
        mask.position.set(
            initialImage.position.x - (initialImage.width * initialImage.anchor.x),
            initialImage.position.y - (initialImage.height * initialImage.anchor.y),
        );

        // "soft edge" brush
        const brush = new PIXI.Graphics();
        brush.beginFill(0xff0000, 0.2);
        brush.drawCircle(0, 0, 50);
        brush.beginFill(0xff0000, 0.6);
        brush.drawCircle(0, 0, 40);
        brush.beginFill(0xff0000);
        brush.drawCircle(0, 0, 20);
        brush.endFill();

        // count pixels before applying mask
        hiddenImage.visible = true;
        const targetPercent = this.percentOpaquePixels(hiddenImage);
        hiddenImage.mask = mask;

        const throttle = 0.100; // seconds after stop scrubbing to check progress

        const complete = () => {
            initialImage.removeListener("pointermove", pointerMoveCallback);
            initialImage.removeListener("pointerdown", pointerDownCallback);
            throttledCheckProgress?.kill();
            hiddenImage.mask = null;
            mask.destroy();
            renderTexture.destroy();
            sound.stop();
            sound2.fadeTo(0, 500);
            TweenLite.delayedCall(0.5, onComplete);
        }

        const checkProgress = () => {
            // counting pixels in mask is easier than in the masked image
            const revealedPercent = this.percentOpaquePixels(renderTexture);
            if ((revealedPercent / targetPercent) >= threshold) {
                complete();
            }
            console.log(Math.round(revealedPercent / targetPercent * 100) + "%");
            sound.pause();
        }

        const throttledCheckProgress = TweenLite.delayedCall(throttle, checkProgress).pause();

        const paint = (pos: { x: number, y: number }) => {
            brush.position.copyFrom(pos);
            Application.instance.renderer.render(brush, renderTexture, false, undefined, false);
        }

        const pointerDownCallback = (e: PIXI.InteractionEvent) => {
            paint(e.data.getLocalPosition(mask));
            if (!sound.isPlaying()) {
                sound.play();
            }
            if (!sound2.isPlaying()) {
                sound2.setLooped(true);
                sound2.play();
                sound2.setVolume(0);
                sound2.fadeTo(1, 5000);
            }
        }

        const pointerMoveCallback = (e: PIXI.InteractionEvent) => {
            // const localPos = e.data.getLocalPosition(hiddenImage);
            // console.log(localPos);
            if (hiddenImage.containsPoint(e.data.global)) {
                paint(e.data.getLocalPosition(mask));
                throttledCheckProgress.restart(true); // check progress 100ms after stop moving cursor
                if (!sound.isPlaying()) {
                    sound.play();
                }
                if (!sound2.isPlaying()) {
                    sound2.setLooped(true);
                    sound2.play();
                    sound2.setVolume(0);
                    sound2.fadeTo(1, 5000);
                }
            }
        }

        const pointerUpCallback = (e: PIXI.InteractionEvent) => {
            throttledCheckProgress.restart(true); // check progress 100ms after stop moving cursor
            sound.pause();
        }

        initialImage.interactive = true;
        initialImage.addListener(HLF.Input.Events.POINTER_MOVE, pointerMoveCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_DOWN, pointerDownCallback);

        initialImage.addListener(HLF.Input.Events.POINTER_UP, pointerUpCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_UP_OUTSIDE, pointerUpCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_CANCEL, pointerUpCallback);
        initialImage.addListener(HLF.Input.Events.POINTER_OUT, pointerUpCallback);
    }

    percentOpaquePixels(image: PIXI.Sprite | PIXI.RenderTexture) {

        if (image instanceof PIXI.RenderTexture) {
            if (image.baseTexture === null) {
                return 0;
            }
        }

        const pixels = Application.instance.renderer.extract.pixels(image);

        const minOpacity = 0 / 255;

        let sum = 0;
        // array goes [red, green, blue, alpha, red, green, blue, alpha, ... etc]
        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] > minOpacity) {
                sum++;
            }
        }

        const length = (pixels.length / 4);
        const percent = sum / length
        return percent;
    }

}